import moment, { Moment } from "moment"
import { createSelector } from "reselect"
import { get } from "lodash"
import { ReduxState } from "model/state"
import { DATE_FORMAT, TIME_FORMAT_TZ, SAME_DAY, Period } from "./dates"
import { Uuid } from "./types/uuid"

export function generateIntradayBatchUrlAt(accountAlias: string, portfolioId: string | Uuid, asOf: string) {
  return `/api/${accountAlias}/p/${portfolioId}/intraday/batches/last-completed?asOf=${encodeURIComponent(asOf)}`
}

const getIntradayCurrentDataIdx = (state: ReduxState): number | null =>
  get(state, "global.intraday.refresh.currentDataIdx", null)

// Returns the instant captured when currentDataIdx last changed — frozen
// until the next tick (socket pulse / manual refresh) so all consumers
// share the same asOf and hit one cache entry. Without this, per-hook
// moment() calls would each generate a unique URL.
export const getIntradayBatchQueryAsOf = createSelector(
  [getIntradayCurrentDataIdx],
  () => moment().format(),
)

export type IntradayRelativeDay = "today" | "yesterday" | `${number} days ago`

export interface IntradayAsOfDisplay {
  currentAsOf: string
  intradayRelativeDay: IntradayRelativeDay
}

// batch.asOf is time-only (TIME_FORMAT_TZ) — moment(batch.asOf) alone is an Invalid Date.
// Combine with batch.date, the same convention computeIntradayAsOfDisplay uses below.
export function getIntradayBatchMoment(batch: { date: string, asOf: string }): Moment {
  return moment(`${batch.date} ${batch.asOf}`, `${DATE_FORMAT} ${TIME_FORMAT_TZ}`)
}

// Explicit reference day, not moment(asOf, TIME_FORMAT_TZ)'s implicit "today" — that can silently
// land on the wrong calendar day whenever local time and UTC currently disagree on what day it is.
export function getBatchMomentOrTimeOnly(
  batch: { date?: string | null, asOf?: string | null } | null | undefined,
  now: Moment = moment(),
): Moment | null {
  if (!batch?.asOf) return null
  const date = batch.date ?? now.format(DATE_FORMAT)
  return getIntradayBatchMoment({ date, asOf: batch.asOf })
}

// A committed batch can lag live pricing by its own commit cadence — a newer batch ID isn't necessarily a later moment.
export function isBatchNewerThanPricing(
  batch: { date?: string | null, asOf?: string | null } | null | undefined,
  pricingAsOf: string | null | undefined,
  now: Moment = moment(),
): boolean {
  const batchMoment = getBatchMomentOrTimeOnly(batch, now)
  if (!batchMoment) return false
  if (!pricingAsOf) return true
  return batchMoment.isAfter(moment(pricingAsOf))
}

export function computeIntradayAsOfDisplay(
  intradayBatchMetadata: { asOf?: string, date?: string } | null | undefined,
  now: Moment = moment(),
): IntradayAsOfDisplay {
  if (!intradayBatchMetadata?.asOf) {
    return { currentAsOf: "-", intradayRelativeDay: "today" }
  }
  const { asOf, date } = intradayBatchMetadata
  if (!date) {
    return { currentAsOf: moment(asOf, TIME_FORMAT_TZ).format("LTS"), intradayRelativeDay: "today" }
  }
  const batchMoment = getIntradayBatchMoment({ date, asOf })
  if (!batchMoment.isValid()) return { currentAsOf: "-", intradayRelativeDay: "today" }
  return {
    currentAsOf: batchMoment.format("LTS"),
    intradayRelativeDay: relativeDayLabel(calendarDayDiff(date, now)),
  }
}

function relativeDayLabel(dayDiff: number): IntradayRelativeDay {
  if (dayDiff <= 0) return "today"
  if (dayDiff === 1) return "yesterday"
  return `${dayDiff} days ago`
}

// Compare two calendar dates by their DATE_FORMAT strings (not moment.format(), which converts to
// local tz and can shift the date for UTC-offset timezones when a timestamp is near midnight UTC).
function calendarDayDiff(dateStr: string, now: Moment): number {
  return moment(now.format(DATE_FORMAT), DATE_FORMAT).diff(moment(dateStr, DATE_FORMAT), "days")
}

// Weekdays elapsed from dateStr (exclusive) through now (inclusive), skipping weekends.
function businessDayDiff(dateStr: string, now: Moment): number {
  const end = moment(now.format(DATE_FORMAT), DATE_FORMAT)
  const cursor = moment(dateStr, DATE_FORMAT)
  let count = 0
  while (cursor.isBefore(end)) {
    cursor.add(1, "day")
    if (cursor.isoWeekday() <= 5) count += 1
  }
  return count
}

export interface IntradayPeriodStaleness {
  isStale: boolean
  // Full text for the period-chip tooltip + the dot's accessible name.
  tooltipLabel: string
}

// Same-day staleness for the period chip — only two-plus business days back trips the dot.
export function getSameDayPerformanceStaleness(
  period: Period | null | undefined,
  performanceStartDate: string | null | undefined,
  performanceEndDate: string | null | undefined,
  modelName: string | null | undefined = null,
  now: Moment = moment(),
): IntradayPeriodStaleness {
  if (
    period !== SAME_DAY
    || !performanceStartDate
    || !performanceEndDate
    || !moment(performanceStartDate).isBefore(moment(performanceEndDate), "day")
    || businessDayDiff(performanceStartDate, now) < 2
  ) {
    return { isStale: false, tooltipLabel: "" }
  }
  const dayDiff = calendarDayDiff(performanceStartDate, now)
  const dateStr = moment(performanceStartDate, DATE_FORMAT).format("M/D")
  const modelData = modelName ? `${modelName} data` : "Modeled data"
  const message = `Displayed Intraday analytics may be using stale data. ${modelData} is ${dayDiff} days old, from ${dateStr}.`
  return {
    isStale: true,
    tooltipLabel: message,
  }
}
